// src/api/auth.ts

import { API_CONFIG } from './config';
import { ApiError } from '../utils/apiClient';
import { clearStoredSession } from '../utils/sessionStorage';

const API_BASE_URL = API_CONFIG.BASE_URL;

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface SignupPayload {
  organization_name: string;
  username: string;
  email: string;
  password: string;
}

export interface LoginResponse {
  access_token: string;
  token_type: string;
  user: {
    id: string;
    username: string;
    email: string;
    full_name?: string;
    email_verified?: boolean;
  };
  organization: {
    id: string;
    name: string;
    slug: string;
  };
  admin: boolean;
}

export interface MeResponse {
  id: string;
  username: string;
  email: string;
  full_name?: string;
  admin: boolean;
  is_admin?: boolean;
  is_internal_admin: boolean;
  email_verified: boolean;
  organization_id?: string;
  organization_name?: string;
  organization_slug?: string;
  iss: string;
}

export interface SignupResponse {
  status: 'verification_required';
  email: string;
  dev_verification_url?: string;
}

export interface ResendVerificationResponse {
  status: 'ok';
  dev_verification_url?: string;
}

const parseError = async (response: Response, fallback: string): Promise<never> => {
  const text = await response.text();
  let message = text || fallback;
  let body: any = text || null;
  try {
    const data = JSON.parse(text);
    body = data;
    const detail = data && data.detail;
    if (typeof detail === 'string') {
      message = detail;
    } else if (detail && typeof detail.message === 'string') {
      message = detail.message;
    } else if (typeof data.message === 'string') {
      message = data.message;
    }
  } catch {}
  throw new ApiError(response.status, response.statusText, message, body);
};

// Backward-compatible no-op token helpers retained during migration.
export const saveToken = (_token: string): void => {};
export const getToken = (): string | null => null;
export const removeToken = (): void => {
  clearStoredSession();
};
export const decodeToken = (): null => null;

export const loginUser = async (
  email: string,
  password: string
): Promise<string> => {
  const response = await fetch(`${API_BASE_URL}/auth/login`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  if (!response.ok) {
    await parseError(response, 'Login failed');
  }
  const data: LoginResponse = await response.json();
  return data.access_token;
};

export const login = async (
  credentials: LoginCredentials
): Promise<LoginResponse> => {
  const response = await fetch(`${API_BASE_URL}/auth/login`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(credentials),
  });
  if (!response.ok) {
    await parseError(response, 'Login failed');
  }
  return response.json();
};

export const signup = async (payload: SignupPayload): Promise<SignupResponse> => {
  const response = await fetch(`${API_BASE_URL}/auth/signup`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    await parseError(response, 'Signup failed');
  }
  return response.json();
};

export const resendVerification = async (
  email: string
): Promise<ResendVerificationResponse> => {
  const response = await fetch(`${API_BASE_URL}/auth/resend-verification`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!response.ok) {
    await parseError(response, 'Unable to resend verification email');
  }
  return response.json();
};

export const verifyEmail = async (token: string): Promise<LoginResponse> => {
  const response = await fetch(
    `${API_BASE_URL}/auth/verify-email?token=${encodeURIComponent(token)}`,
    {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    }
  );
  if (!response.ok) {
    await parseError(response, 'Unable to verify email');
  }
  return response.json();
};

export const refreshSession = async (): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) {
    await parseError(response, 'Session refresh failed');
  }
};

export const logout = async (): Promise<void> => {
  try {
    await fetch(`${API_BASE_URL}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
  } catch {
    // best effort
  } finally {
    clearStoredSession();
    window.location.href = '/login';
  }
};

export const getCurrentUser = async (_token?: string): Promise<MeResponse> => {
  const response = await fetch(`${API_BASE_URL}/auth/me`, {
    method: 'GET',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) {
    await parseError(response, `Failed to get current user (${response.status})`);
  }
  return response.json();
};

export const getMe = async (): Promise<MeResponse> => getCurrentUser();

export const isTokenExpired = (): boolean => false;
export const isAuthenticated = (): boolean => !!localStorage.getItem('authData');
export const hasMultiTenantToken = (): boolean => !!localStorage.getItem('authData');
